import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Search, Trash2, ArrowRight, School, GraduationCap, BookOpen, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const ComparisonModal = ({ institutions, onClose, onCompare }) => {
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInstitutions, setFilteredInstitutions] = useState(institutions);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = institutions.filter(inst =>
        inst.name?.toLowerCase().includes(term) ||
        inst.address?.city?.toLowerCase().includes(term) ||
        inst.type?.toLowerCase().includes(term)
      );
      setFilteredInstitutions(filtered);
    } else {
      setFilteredInstitutions(institutions);
    }
  }, [searchTerm, institutions]);

  const handleSelectInstitution = (institution) => {
    if (selectedInstitutions.find(inst => inst.id === institution.id)) {
      toast.error('Institution already selected');
      return;
    }

    if (selectedInstitutions.length >= 5) {
      toast.error('You can compare up to 5 institutions at a time');
      return;
    }

    setSelectedInstitutions([...selectedInstitutions, institution]);
    toast.success(`${institution.name} added to comparison`);
  };

  const handleRemoveInstitution = (institutionId) => {
    setSelectedInstitutions(selectedInstitutions.filter(inst => inst.id !== institutionId));
  };

  const handleCompare = () => {
    if (selectedInstitutions.length < 1) {
      toast.error('Please select at least 1 institution to compare');
      return;
    }
    onCompare(selectedInstitutions);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'school': return <School className="w-4 h-4" />;
      case 'college': return <GraduationCap className="w-4 h-4" />;
      case 'coaching': return <BookOpen className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'school': return 'bg-blue-100 text-blue-800';
      case 'college': return 'bg-purple-100 text-purple-800';
      case 'coaching': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Compare Institutions</h2>
            <p className="text-blue-100 text-sm mt-1">Select up to 5 institutions to compare</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Selected Institutions */}
        {selectedInstitutions.length > 0 && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">
                Selected ({selectedInstitutions.length}/5)
              </h3>
              <button
                onClick={handleCompare}
                disabled={selectedInstitutions.length < 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                  selectedInstitutions.length >= 1
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Compare Now
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedInstitutions.map((inst) => (
                <div
                  key={inst.id}
                  className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm"
                >
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(inst.type)} flex items-center gap-1`}>
                    {getTypeIcon(inst.type)}
                    {inst.type}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{inst.name}</span>
                  <button
                    onClick={() => handleRemoveInstitution(inst.id)}
                    className="text-red-500 hover:text-red-700 ml-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, city, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Institution List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filteredInstitutions.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No institutions found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredInstitutions.map((institution) => {
                const isSelected = selectedInstitutions.find(inst => inst.id === institution.id);
                return (
                  <div
                    key={institution.id}
                    className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                    }`}
                    onClick={() => !isSelected && handleSelectInstitution(institution)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex-shrink-0 overflow-hidden">
                        {institution.profilePicture || institution.logo ? (
                          <img
                            src={institution.profilePicture || institution.logo}
                            alt={institution.name}
                            className="w-full h-full object-cover"
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{institution.name}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(institution.type)} flex items-center gap-1 flex-shrink-0`}>
                            {getTypeIcon(institution.type)}
                            {institution.type}
                          </span>
                        </div>
                        {institution.address && (
                          <p className="text-xs text-gray-600 mb-2">
                            {institution.address.city}
                            {institution.address.state && `, ${institution.address.state}`}
                          </p>
                        )}
                        {!isSelected && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectInstitution(institution);
                            }}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                          >
                            <Plus className="w-3 h-3" />
                            Add to Compare
                          </button>
                        )}
                        {isSelected && (
                          <span className="text-xs text-blue-600 font-semibold">✓ Selected</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ComparisonModal;
